import { Suspense, useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { addDays, format } from 'date-fns';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

import {
  Button,
  Dialog,
  DialogState,
  Drawer,
  Flex,
  FormField,
  Input,
  Select,
  Skeleton,
  Switch,
  Text,
  useDialog,
} from '@components/primitives';

import { AdminTeamObject } from '@components/features/Admin/types';

import {
  TeamFeature,
  ToggleFeatureWithConfigOptions,
  useTeamFeatures,
  useToggleFeatureWithConfig,
} from './queries';

import css from './styles.module.css';

const DEFAULT_FEATURE_CONFIGS: Record<string, Record<string, any>> = {
  'page-protection': {
    tier: 'pro',
  },
};

interface FeatureToggleProps {
  team: AdminTeamObject;
  state: DialogState;
}

export function FeatureToggle({ team, state }: FeatureToggleProps) {
  return (
    <Drawer state={state}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>{team.name} Features</Drawer.Title>
          <Drawer.Close />
        </Drawer.Header>

        <div className="p-5">
          <Suspense fallback={<LoadingFeatures />}>
            <FeatureToggleContent team={team} />
          </Suspense>
        </div>
      </Drawer.Content>
    </Drawer>
  );
}

function FeatureToggleContent({ team }: { team: AdminTeamObject }) {
  const { data } = useTeamFeatures(team.uuid);
  const features = data || [];

  return features.map((config) => (
    <Feature key={config.feature.id} team={team} config={config} />
  ));
}

function LoadingFeatures() {
  return (
    <>
      <div className={css.row}>
        <Skeleton width={80} height={12} className="mb-2" />
        <Skeleton width="100%" height={8} className="mb-2" />
        <Skeleton width="100%" height={8} className="mb-2" />
        <Skeleton width="70%" height={8} />
      </div>
      <div className={css.row}>
        <Skeleton width={80} height={12} className="mb-2" />
        <Skeleton width="100%" height={8} className="mb-2" />
        <Skeleton width="100%" height={8} className="mb-2" />
        <Skeleton width="70%" height={8} />
      </div>
      <div className={css.row}>
        <Skeleton width={80} height={12} className="mb-2" />
        <Skeleton width="100%" height={8} className="mb-2" />
        <Skeleton width="100%" height={8} className="mb-2" />
        <Skeleton width="70%" height={8} />
      </div>
    </>
  );
}

function Feature({
  team,
  config,
}: {
  team: AdminTeamObject;
  config: TeamFeature;
}) {
  const dialog = useDialog();
  const mutation = useToggleFeatureWithConfig(team.uuid, config.feature.id);

  const onToggleChange = (enabled: boolean) => {
    if (enabled) {
      const options: ToggleFeatureWithConfigOptions = { enabled: true };
      if (config.feature.name in DEFAULT_FEATURE_CONFIGS) {
        options.config = DEFAULT_FEATURE_CONFIGS[config.feature.name];
      }
      mutation.mutate(options, {
        onSuccess() {
          toast.success(`${config.feature.name} enabled for ${team.name}`);
        },
        onError() {
          toast.error(
            `Failed to enable ${config.feature.name} for ${team.name}`
          );
        },
      });
    } else {
      dialog.open();
    }
  };

  const handleConfirmDisable = async () => {
    await mutation.mutateAsync(
      { enabled: false },
      {
        onSuccess() {
          toast(`${config.feature.name} disabled for ${team.name}`);
        },
        onError() {
          toast.error(
            `Failed to disable ${config.feature.name} for ${team.name}`
          );
        },
      }
    );
  };

  return (
    <>
      <Dialog.Confirmation
        state={dialog}
        title={`Disable ${config.feature.name}`}
        onConfirm={handleConfirmDisable}
      >
        Are you sure you want to disable {config.feature.name} for {team.name}?
        They will no longer have access to this feature.
      </Dialog.Confirmation>
      <div key={config.feature.id} className={css.row}>
        <Flex justify="between" align="center" gap="16px">
          <div>
            <Text weight={600} leading="lg">
              {config.feature.name}
            </Text>
            <Text leading="md" size="sm" color="var(--grey-60)">
              {config.feature.description}
            </Text>
          </div>
          <Switch
            size="lg"
            checked={config.enabled}
            onCheckedChange={onToggleChange}
            aria-label={
              config.enabled
                ? `Disable feature: ${config.feature.name}`
                : `Enable feature: ${config.feature.name}`
            }
          />
        </Flex>

        {config.feature.name === 'page-protection' && config.enabled && (
          <PageProtectionFeature team={team} config={config} />
        )}
      </div>
    </>
  );
}

const ppFeatureSchema = z.object({
  tier: z.enum(['trial', 'pro', 'scale']),
  endDate: z.coerce.date().optional(),
});

function PageProtectionFeature({
  team,
  config,
}: {
  team: AdminTeamObject;
  config: TeamFeature;
}) {
  const mutation = useToggleFeatureWithConfig(team.uuid, config.feature.id);

  const defaultValues = useMemo(() => {
    const endDate = config.config?.endDate
      ? new Date(config.config.endDate)
      : addDays(new Date(), 14);

    return {
      tier: config.config?.tier || 'pro',
      endDate,
    };
  }, [config.config]);

  const methods = useForm({
    resolver: zodResolver(ppFeatureSchema),
    defaultValues,
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues]);

  const { isDirty, isValid, isSubmitting } = methods.formState;

  const submit = methods.handleSubmit(async (data) => {
    const feature: Record<string, any> = { tier: data.tier };
    if (data.tier === 'trial') {
      feature.endDate = data.endDate;
    }

    await mutation.mutateAsync({ enabled: true, config: feature });

    toast.success(`${config.feature.name} updated for ${team.name}`);
  });

  const tier = methods.watch('tier');
  const endDate = format(methods.watch('endDate'), "yyyy-MM-dd'T'HH:mm");

  return (
    <form className={css.card} onSubmit={submit}>
      <FormField label="Tier">
        <Select
          value={tier}
          onChange={(value) =>
            methods.setValue('tier', value, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        >
          <Select.Option value="trial">Trial</Select.Option>
          <Select.Option value="pro">Pro</Select.Option>
          <Select.Option value="scale">Scale</Select.Option>
        </Select>
      </FormField>

      {tier === 'trial' && (
        <FormField label="Trial End Date">
          <Input
            type="datetime-local"
            value={endDate}
            onChange={(e) => {
              const date = new Date(e.target.value);
              methods.setValue('endDate', date, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
          />
        </FormField>
      )}

      <Button
        type="submit"
        size="sm"
        variant="primary"
        loading={isSubmitting}
        disabled={!isDirty || !isValid}
      >
        Update
      </Button>
    </form>
  );
}
